import { modelsApi } from '@/api/models.api';
import { projectsApi } from '@/api/projects.api';
import { MOTION } from '@/components/common/motion';
import { CARD, DROP_ZONE, DROP_ZONE_ACTIVE } from '@/components/common/surfaces';
import { extractDroppedFiles } from '@/utils/dropFiles';
import { sanitizeFileName, validateStpFile } from '@/utils/validators';
import { Plus, UploadSimple } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

type UploadPhase = 'creating' | 'uploading' | 'processing';

interface CreateProjectCardProps {
  compact?: boolean;
  /** When provided, upload runs in background via queue; card stays ready for next upload and no redirect. */
  onStartUpload?: (file: File) => void;
  /** Rule set the new project is machined with; it cannot be changed once processing has run. */
  holeRuleSet?: string | null;
}

export const CreateProjectCard: FC<CreateProjectCardProps> = ({
  compact = false,
  onStartUpload,
  holeRuleSet = null,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('creating');
  const [currentStep, setCurrentStep] = useState<string>('');
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUploading) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current && e.relatedTarget instanceof Node) {
      if (dropZoneRef.current.contains(e.relatedTarget)) return;
    }
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const pickFile = (file: File): boolean => {
    const validation = validateStpFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return false;
    }
    return true;
  };

  const handleFileChosen = async (file: File) => {
    if (!pickFile(file)) return;

    if (onStartUpload) {
      onStartUpload(file);
      return;
    }

    setIsUploading(true);
    setUploadPhase('creating');
    let projectId: string | null = null;

    try {
      const title = sanitizeFileName(file.name);
      const { uid } = await projectsApi.create({
        title,
        // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
        hole_rule_set: holeRuleSet,
      });
      projectId = uid;

      setUploadPhase('uploading');
      setCurrentStep(t('projects.uploadingEmbedded'));

      let taskId: string;
      try {
        const submit = await modelsApi.uploadEmbeddedBackground(projectId, file);
        taskId = submit.uid;
      } catch (submitError) {
        await projectsApi.delete(projectId).catch(() => undefined);
        projectId = null;
        throw submitError;
      }

      await modelsApi.trackProgressSSE(taskId, (p) => {
        setCurrentStep(p.current_step || t('projects.uploadingEmbedded'));
        setUploadPhase(p.progress < 10 ? 'uploading' : 'processing');
      });

      toast.success(t('projects.projectCreated'));
      setTimeout(() => {
        if (projectId) navigate(`/project/${projectId}`);
      }, 300);
    } catch (error) {
      console.error('Error during project creation/upload:', error);

      let errorMessage = projectId ? t('projects.failedToUpload') : t('projects.failedToCreate');

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { status?: number; data?: { detail?: string } };
          code?: string;
        };
        const status = axiosError.response?.status;
        const code = axiosError.code;
        const detail = axiosError.response?.data?.detail;

        if (code === 'ECONNREFUSED' || code === 'ENOTFOUND' || code === 'ERR_NETWORK' || code === 'ECONNABORTED') {
          errorMessage = t('projects.backendUnavailable');
        } else if (status === 400 && detail) {
          // Backend embedded-stock validation rejection (no "stock" solid,
          // model protrudes beyond stock, unsupported colors, …) — surface verbatim.
          errorMessage = detail;
        } else if (status && status >= 500 && status < 600) {
          errorMessage = t('projects.serverError');
        } else if (status === 502 || status === 503 || status === 504) {
          errorMessage = t('projects.backendUnavailable');
        } else if (status === 401) {
          errorMessage = t('projects.authError');
        } else if (status === 404) {
          errorMessage = projectId ? t('projects.uploadError') : t('projects.failedToCreate');
        } else if (detail) {
          errorMessage = detail;
        }
      } else if (error instanceof Error) {
        const message = error.message.toLowerCase();
        if (message.includes('apt generation failed')) {
          errorMessage = error.message.replace(/^Failed:\s*/, '');
        } else if (
          message.includes('network') ||
          message.includes('fetch') ||
          message.includes('connection') ||
          message.includes('econnrefused') ||
          message.includes('enotfound') ||
          message.includes('err_network')
        ) {
          errorMessage = projectId ? t('projects.uploadConnectionError') : t('projects.createConnectionError');
        } else if (message.includes('timeout') || message.includes('too long') || message.includes('timed out')) {
          errorMessage = projectId ? t('projects.uploadTimeout') : t('projects.createTimeout');
        } else if (message.includes('401') || message.includes('unauthorized') || message.includes('authentication')) {
          errorMessage = t('projects.authError');
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
      setCurrentStep('');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isUploading) {
      toast.warning(t('projects.uploadInProgress'));
      return;
    }
    const { files, hadDirectory, receivedAnything } = extractDroppedFiles(e.dataTransfer);
    const file = files.find((f) => f.name.toLowerCase().endsWith('.stp'));
    if (!file) {
      if (hadDirectory) {
        toast.error(t('validation.dropFolderStp'));
      } else if (!receivedAnything) {
        toast.error(t('validation.dropNoFileReceived'));
      } else {
        toast.error(t('validation.stpFileRequired'));
      }
      return;
    }
    void handleFileChosen(file);
  };

  const handleClick = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    void handleFileChosen(file);
  };

  const phaseLabel =
    uploadPhase === 'creating'
      ? t('projects.creating')
      : uploadPhase === 'uploading'
        ? t('projects.uploadingEmbedded')
        : currentStep === 'Generating APT hole visualizations'
          ? t('projects.generatingAptHoleVisualizations')
          : currentStep || t('projects.processing');

  /* The indeterminate bar replaces the old ring: the backend reports progress in long, uneven
     jumps, so a percentage that sat at 8 % and then leapt to 90 % read as a stall. A bar that
     simply moves says the same true thing — work is happening — without claiming to know how
     much is left. */
  const progressBar = (
    <div className="flex w-full flex-col gap-2">
      <p className="m-0 text-sm font-medium text-gray-900 [text-wrap:pretty]">{phaseLabel}</p>
      <div className="h-1 w-full overflow-hidden rounded-full bg-gray-100">
        <div className="indeterminate-progress-bar h-full rounded-full bg-primary" />
      </div>
      {!compact && <p className="m-0 text-sm text-gray-500">{t('projects.mayTakeTime')}</p>}
    </div>
  );

  const tile = (
    <span
      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gray-100 ${
        isDragging ? 'text-primary' : 'text-gray-400'
      } ${MOTION}`}
    >
      {isDragging ? <UploadSimple size={24} aria-hidden="true" /> : <Plus size={24} aria-hidden="true" />}
    </span>
  );

  const title = isDragging ? t('projects.dropHere') : t('projects.addNew');

  /* `!rounded-2xl` because `DROP_ZONE` is the 12 px step a drop target takes when it nests inside
     a card; here the zone *is* a card — it sits in the grid next to `ProjectCard`s — so it has to
     take the card's own 16 px. Two `rounded-*` utilities on one element are otherwise settled by
     stylesheet order rather than by the order they appear here. */
  const zoneClasses = `${isDragging || isUploading ? DROP_ZONE_ACTIVE : DROP_ZONE} !rounded-2xl`;

  const interactionClasses = `${MOTION} ${
    isUploading ? 'cursor-wait' : 'cursor-pointer'
  } focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`;

  return (
    <>
      {compact ? (
        /* Above the table the card is a row rather than a panel: the table is the page here, and
           a 300 px dashed box on top of it would out-weigh the thing it introduces. */
        <div
          ref={dropZoneRef}
          className={`${CARD} flex items-center gap-4 p-4 hover:border-gray-300 ${interactionClasses} ${
            isDragging ? 'border-primary bg-primary/5' : ''
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          tabIndex={0}
          role="button"
        >
          {isUploading ? (
            progressBar
          ) : (
            <>
              {tile}
              <span className="flex min-w-0 flex-col gap-1">
                <span className="truncate text-base font-semibold text-gray-900">{title}</span>
                <span className="truncate text-sm text-gray-500">{t('projects.clickToOpen')}</span>
              </span>
              <span className="ml-auto hidden shrink-0 items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 sm:inline-flex">
                <UploadSimple size={16} aria-hidden="true" />
                {t('projects.chooseFile')}
              </span>
            </>
          )}
        </div>
      ) : (
        <div
          ref={dropZoneRef}
          className={`flex min-h-[300px] flex-col items-center justify-center gap-4 p-8 text-center ${zoneClasses} ${interactionClasses}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          tabIndex={0}
          role="button"
        >
          {isUploading ? (
            progressBar
          ) : (
            <>
              {tile}
              <span className="flex flex-col gap-2">
                <span className="block text-base font-semibold text-gray-900">{title}</span>
                <span className="block max-w-[280px] text-sm text-gray-500 [text-wrap:pretty]">
                  {t('projects.clickToOpen')}
                </span>
              </span>
            </>
          )}
        </div>
      )}

      <input ref={fileInputRef} type="file" accept=".stp" className="hidden" onChange={handleInputChange} />
    </>
  );
};
